import cookie from '@nextories/vue-core-components/src/utils/helpers/cookie';
import type { Store } from 'pinia';
import type { Router } from 'vue-router';
import axios, { type AxiosResponse, type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import routerBase from '../../router';
import { useAuthStore } from '@/stores';

export default function setup(
  router?: Router,
  authStore?: Store,
  env?: string,
  authorization?: string
) {
  router = router || routerBase;
  authStore = authStore || useAuthStore;
  env = env || import.meta.env.VITE_APP_API;
  // Set api url
  const apiCall = axios.create({
    baseURL: env,
  });

  apiCall.defaults.headers.post['Content-Type'] = 'application/json';

  apiCall.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      config.headers = config.headers || {};
      if (authorization) {
        config.headers.authorization = authorization;
      } else {
        const token = cookie.getCookie(import.meta.env.VITE_APP_COOKIE_TOKEN_NAME);
        if (token) {
          config.headers.authorization = `Bearer ${token}`;
        }
      }
      return config;
    },
    (error: AxiosError) => Promise.reject(error)
  );

  apiCall.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      const currentRoute = router.currentRoute.value;
      const currentAuthStore = authStore();
      if (error?.response?.status === 401) {
        let redirectedToLogin = false;
        cookie.deleteCookie(import.meta.env.VITE_APP_COOKIE_TOKEN_NAME);
        currentAuthStore.unsetIsLogged?.();
        if (currentRoute.name !== 'login') {
          const query: Record<string, string> = {};
          if (currentRoute.meta?.authRequired) {
            query.u = encodeURIComponent(currentRoute.path);
          }
          if (error.response.data?.code === 'token_not_valid') {
            query.error = error.response.data.code;
          }
          router.push({ name: 'login', query }).catch(() => { });
          redirectedToLogin = true;
        }
        if (error.response) {
          error.response.redirectedToLogin = redirectedToLogin;
          error.response.isAuthenticationError = true;
        }
        return Promise.reject(error);
      }
      if (!error?.response) {
        const isOptions = error?.config?.method === 'options';
        const isNetworkError = error?.code === 'ERR_NETWORK' || error?.message === 'Network Error';
        if (!isOptions && isNetworkError) {
          let redirectedToMaintenance = false;
          if (currentRoute.name !== 'maintenance') {
            const query: Record<string, string> = {
              u: encodeURIComponent(currentRoute.fullPath),
            };
            router.push({ name: 'maintenance', query }).catch(() => { });
            redirectedToMaintenance = true;
          }
          if (error.request) {
            error.request.redirectedToMaintenance = redirectedToMaintenance;
            error.request.dontRemoveCookie = true;
          }
        }
        return Promise.reject(error);
      }
      if (error?.response?.status === 503) {
        if (error?.config?.method === 'options') {
          return Promise.reject(error);
        }
        let redirectedToMaintenance = false;
        // MAINTENANCE MODE
        if (currentRoute.name !== 'maintenance') {
          const query: Record<string, string> = {
            u: encodeURIComponent(currentRoute.fullPath),
          };
          router.push({ name: 'maintenance', query }).catch(() => { });
          redirectedToMaintenance = true;
        }
        if (error.response) {
          error.response.redirectedToMaintenance = redirectedToMaintenance;
          error.response.dontRemoveCookie = true;
        }
        return Promise.reject(error);
      }
      if (error?.response?.status && error?.response?.data) {
        // Handle error with data
        return Promise.reject(error);
      }
      return Promise.reject(error);
    }
  );
  return apiCall;
}
